import React from 'react';
import { Box } from '@radix-ui/themes';

import { GraphQLTable, Column } from '../../components/GraphQLTable';
import HashShortener from '../../components/HashShortener';
import ViewButton from '../../components/ViewButton';

import {
  GetBlockTransactionsDocument,
  GetBlockTransactionsQuery,
  GetBlockTransactionsQueryVariables,
} from '../../graphql/queries/GetBlockTransactions.generated';

interface BlockTransactionsTableProps {
  blockNumber: number;
}

const BlockTransactionsTable: React.FC<BlockTransactionsTableProps> = ({
  blockNumber,
}) => {
  const columns: Column<
    NonNullable<
      NonNullable<
        GetBlockTransactionsQuery['block']
      >['transactions']['edges'][0]['node']
    >
  >[] = [
    {
      header: 'Hash',
      accessor: 'hash',
      render: ({ value }) => <HashShortener hash={value} />,
      filterable: true,
      filterField: 'hash',
      filterConfig: {
        type: 'text',
        placeholder: 'Filter by hash...',
      },
      sortable: true,
      sortField: 'hash',
    },
    {
      header: 'From',
      accessor: 'from',
      render: ({ value }) => (
        <HashShortener hash={value} link={`/addresses/${value}`} />
      ),
      filterable: true,
      filterField: 'from',
      filterConfig: {
        type: 'text',
        placeholder: 'Filter by from address...',
      },
      sortable: true,
      sortField: 'from',
    },
    {
      header: 'To',
      accessor: 'to',
      render: ({ value }) =>
        value ? (
          <HashShortener hash={value} link={`/addresses/${value}`} />
        ) : (
          <span>Contract Creation</span>
        ),
      filterable: true,
      filterField: 'to',
      filterConfig: {
        type: 'text',
        placeholder: 'Filter by to address...',
      },
      sortable: true,
      sortField: 'to',
    },
    {
      header: 'Value (ETH)',
      accessor: 'valueEth',
      render: ({ value }) => (
        <span style={{ fontFamily: 'monospace', fontWeight: '500' }}>
          {parseFloat(value).toFixed(6)} ETH
        </span>
      ),
      filterable: true,
      filterField: 'valueEth',
      filterConfig: {
        type: 'number',
        placeholder: 'Filter by ETH value...',
      },
      sortable: true,
      sortField: 'valueEth',
    },
    {
      header: 'Value (USD)',
      accessor: 'valueUsd',
      render: ({ value }) => (
        <span style={{ fontFamily: 'monospace', fontWeight: '500' }}>
          $
          {parseFloat(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
      filterable: true,
      filterField: 'valueUsd',
      filterConfig: {
        type: 'number',
        placeholder: 'Filter by USD value...',
      },
      sortable: true,
      sortField: 'valueUsd',
    },
    {
      header: 'Gas',
      accessor: 'gas',
      render: ({ value }) => (
        <span style={{ fontFamily: 'monospace' }}>
          {value.toLocaleString()}
        </span>
      ),
      filterable: true,
      filterField: 'gas',
      filterConfig: {
        type: 'number',
        placeholder: 'Filter by gas...',
      },
      sortable: true,
      sortField: 'gas',
    },
    // {
    // 	header: "Network Status",
    // 	accessor: "status",
    // 	render: ({ value }) => <TransactionStatusBadge status={value} />
    // },
    // {
    // 	header: "Ingest Status",
    // 	accessor: "ingestStatus",
    // 	render: ({ value }) => <IngestStatusBadge ingestStatus={value} />
    // },
    {
      header: null,
      render: ({ row }) => (
        <ViewButton to={`/transactions/${row.hash}`} text="View Transaction" />
      ),
      style: { justifyContent: 'center' },
    },
  ];

  return (
    <Box>
      <GraphQLTable<
        GetBlockTransactionsQuery,
        GetBlockTransactionsQueryVariables,
        'block.transactions',
        NonNullable<
          NonNullable<GetBlockTransactionsQuery['block']>
        >['transactions']['edges'][0]['node']
      >
        query={GetBlockTransactionsDocument}
        columns={columns}
        variables={{ number: blockNumber }}
        connectionPath="block.transactions"
        loadingMessage="Fetching latest transactions..."
        errorMessage="Failed to fetch transactions."
        noDataMessage="No transactions available."
      />
    </Box>
  );
};

export default BlockTransactionsTable;
